import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { ScreenConfig } from "../shared/protocol.ts";
import { Dashboard } from "./components/Dashboard.tsx";
import { ScenarioScreen } from "./components/ScenarioScreen.tsx";
import { IdleScreen } from "./components/IdleScreen.tsx";
import { ImageScreen } from "./components/ImageScreen.tsx";
import { PresentationScreen } from "./components/PresentationScreen.tsx";
import { AlertScreen } from "./components/AlertScreen.tsx";
import { StatusDot } from "./components/StatusDot.tsx";
import { VideoScreen } from "./components/VideoScreen.tsx";
import { WebScreen } from "./components/WebScreen.tsx";
import { CallScreen } from "./components/CallScreen.tsx";
import { useDisplay } from "./useDisplay.ts";
import { completedPlayback, rememberPlayback, shouldShowVideo } from "./videoCompletion.ts";

const preview = new URLSearchParams(location.search).get('preview') === '1';

/** Presentation branding is RAFED; language direction remains configurable. */
function useDirection(config: ScreenConfig) {
  useEffect(() => {
    const root = document.documentElement;
    // `auto` means "let each string decide", which is right for a bilingual
    // room; removing the attribute rather than writing "auto" keeps the
    // per-element dir="auto" the components already set in charge.
    if (config.direction === "auto") root.removeAttribute("dir");
    else root.setAttribute("dir", config.direction);
  }, [config.direction]);
}

export default function App() {
  const { state, connected, connection } = useDisplay();
  const [completedVideo, setCompletedVideo] = useState(completedPlayback);
  const finishVideo = () => {
    const id = state.video?.playbackId;
    if (id) { rememberPlayback(id); setCompletedVideo(id); }
  };
  useDirection(state.config);
  useEffect(() => {
    if (preview && window.parent !== window) window.parent.postMessage({
      type:'rafed.preview.status', connection,
      title:state.presentationSlide?.arabicTitle || state.title ||
        (state.mode === 'call' ? 'Customer-service call' : state.mode === 'idle' ? 'Welcome screen' : state.mode),
    }, window.location.origin);
  }, [connection, state]);

  return (
    <div className="h-full w-full">
      {!preview && state.config.showStatus ? (
        <StatusDot connection={connection} connected={connected} robotOnline={state.robot.online} speaking={state.robot.speaking} />
      ) : null}

      {connection === "access-required" ? (
        <div role="alert" dir="rtl" className="fixed bottom-8 inset-x-6 z-50 mx-auto max-w-xl rounded-2xl bg-white p-5 text-center text-slate-800 shadow-lg">
          افتح رابط الشاشة الخاص من صفحة إعداد المحادثة للاتصال بالعرض.
        </div>
      ) : null}

      {/* One mode on screen at a time; the crossfade hides the swap. */}
      <AnimatePresence mode="wait">
        {state.presentationSlide ? (
          <PresentationScreen key={state.presentationSlide.arabicTitle} slide={state.presentationSlide} />
        ) : state.mode === "alert" && state.alert ? (
          <AlertScreen key={state.alert.id} alert={state.alert} />
        ) : state.mode === "call" && state.call ? (
          <motion.div key="call" className="h-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CallScreen call={state.call} speaking={state.robot.speaking} connected={connected && state.robot.online} />
          </motion.div>
        ) : state.mode === "video" && state.video && shouldShowVideo(state.video, completedVideo) ? (
          <motion.div key="video" className="h-full">
            <VideoScreen key={state.video.playbackId || state.video.url}
              url={state.video.url} title={state.video.title} forceMuted={preview || state.video.muted}
              preferSound={state.video.preferSound} returnToSlide={state.video.returnToSlide}
              onEnded={finishVideo} />
          </motion.div>
        ) : state.mode === "page" && state.page ? (
          <motion.div key="page" className="h-full">
            <WebScreen url={state.page.url} title={state.page.title} preview={preview} />
          </motion.div>
        ) : state.mode === "image" && state.image ? (
          <motion.div key="image" className="h-full">
            <ImageScreen url={state.image.url} title={state.image.title} />
          </motion.div>
        ) : state.mode === "dashboard" && state.tiles.length > 0 ? (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-full"
          >
            <Dashboard
              title={state.title || state.config.dashboardFallbackTitle}
              tiles={state.tiles}
            />
          </motion.div>
        ) : (
          <motion.div key="idle" exit={{ opacity: 0 }} className="h-full">
            <>{state.scenarioBackground?.video ? <ScenarioScreen key={state.scenarioBackground.video} slide={state.scenarioBackground} /> : <IdleScreen config={state.config} />}</>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
